// src/pages/GalleryGrid.js
import React, { useEffect, useState } from "react";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { Helmet } from "react-helmet"; 
import "../styles/Gallery.css";

export default function GalleryGrid() {
  const [images, setImages] = useState([]);
  const [modalImg, setModalImg] = useState(null);

  useEffect(() => {
    async function fetchGalleryImages() {
      try {
        const folderRef = ref(storage, "adminGallery");
        const result = await listAll(folderRef);
        const urls = await Promise.all(
          result.items.map((itemRef) => getDownloadURL(itemRef))
        );
        setImages(urls);
      } catch (err) {
        console.error("Failed to load gallery images:", err);
      }
    }
    fetchGalleryImages();
  }, []);

  function openModal(src) {
    setModalImg(src);
    document.body.style.overflow = "hidden";
  }
  function closeModal() {
    setModalImg(null);
    document.body.style.overflow = "";
  }

  useEffect(() => {
    if (!modalImg) return;
    const handler = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [modalImg]);

  if (images.length === 0) {
    return (
      <div className="gallery-grid-loading">
        Loading gallery...
      </div>
    );
  }

  return (
    <main className="gallery-grid-section">
      <Helmet>
        <title>Golden Retriever Puppy Gallery | DeClerck’s Goldens</title>
        <meta
          name="description"
          content="Browse our gallery of Golden Retriever puppies and family moments at DeClerck’s Goldens. See why our pups are loved by families everywhere."
        />

        {/* Open Graph */}
        <meta property="og:title" content="Golden Retriever Puppy Gallery | DeClerck’s Goldens" />
        <meta property="og:description" content="See our Golden Retriever puppies and family moments in the gallery at DeClerck’s Goldens." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.declercksgoldens.com/gallery" />
        <meta property="og:image" content={images[0] || "https://www.declercksgoldens.com/hero-bg-square.png"} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Golden Retriever Puppy Gallery | DeClerck’s Goldens" />
        <meta name="twitter:description" content="Golden Retriever puppies and family moments in our gallery." />
        <meta name="twitter:image" content={images[0] || "https://www.declercksgoldens.com/hero-bg-square.png"} />

        {/* Canonical */}
        <link rel="canonical" href="https://www.declercksgoldens.com/gallery" />
      </Helmet>

      <div className="gallery-grid-header">
        <h2 className="gallery-grid-title">All Our Golden Memories</h2>
        <p className="gallery-grid-subtitle">
          Every moment on our farm is special. Browse the gallery below to see some of our favorite golden retriever memories!
        </p>
      </div>
      <div className="gallery-grid-list">
        {images.map((src, idx) => (
          <div
            className="gallery-grid-card"
            key={idx}
            onClick={() => openModal(src)}
            tabIndex={0}
            aria-label={`Gallery image ${idx + 1}`}
            onKeyDown={e =>
              (e.key === "Enter" || e.key === " ") && openModal(src)
            }
          >
            <img
              src={src}
              alt={`Golden retriever puppy ${idx + 1}`}
              loading="lazy"
              draggable={false}
            />
          </div>
        ))}
      </div>

      {/* Modal / Lightbox */}
      {modalImg && (
        <div
          className="gallery-grid-modal-backdrop"
          onClick={closeModal}
          tabIndex={-1}
          aria-modal="true"
          role="dialog"
        >
          <span
            className="gallery-grid-modal-close"
            aria-label="Close"
            onClick={closeModal}
            tabIndex={0}
            onKeyDown={e => (e.key === "Enter" || e.key === " ") && closeModal()}
          >
            &times;
          </span>
          <img
            className="gallery-grid-modal-img"
            src={modalImg}
            alt="Large preview"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </main>
  );
}
